export const AGE_BANDS = [
  'UNDER_13',
  'AGE_13_17',
  'AGE_18_24',
  'AGE_25_34',
  'AGE_35_44',
  'AGE_45_PLUS',
] as const;

export type AgeBand = (typeof AGE_BANDS)[number];

/**
 * `PREFER_NOT_TO_SAY` is a declared answer, distinct from `null`, which means
 * never asked. Collapsing the two would make the prompt re-appear for someone
 * who has already declined.
 */
export const GENDERS = ['FEMALE', 'MALE', 'NON_BINARY', 'PREFER_NOT_TO_SAY'] as const;

export type Gender = (typeof GENDERS)[number];

export const AGE_BAND_LABELS: Record<AgeBand, string> = {
  UNDER_13: 'Under 13',
  AGE_13_17: '13 to 17',
  AGE_18_24: '18 to 24',
  AGE_25_34: '25 to 34',
  AGE_35_44: '35 to 44',
  AGE_45_PLUS: '45 and over',
};

export const GENDER_LABELS: Record<Gender, string> = {
  FEMALE: 'Female',
  MALE: 'Male',
  NON_BINARY: 'Non-binary',
  PREFER_NOT_TO_SAY: 'Prefer not to say',
};

export function isAgeBand(value: unknown): value is AgeBand {
  return typeof value === 'string' && (AGE_BANDS as readonly string[]).includes(value);
}

export function isGender(value: unknown): value is Gender {
  return typeof value === 'string' && (GENDERS as readonly string[]).includes(value);
}

/**
 * ISO 3166-1 alpha-2. Names are not stored alongside them: `Intl.DisplayNames`
 * renders them at the point of display, which keeps the list to codes and
 * localises for free.
 */
export const COUNTRY_CODES = [
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
  'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
  'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE',
  'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF',
  'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
  'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM',
  'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC',
  'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
  'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA',
  'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG',
  'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO',
  'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
  'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
] as const;

export type CountryCode = (typeof COUNTRY_CODES)[number];

const COUNTRY_CODE_SET = new Set<string>(COUNTRY_CODES);

/** Accepts any case; the canonical stored form is upper-case. */
export function normalizeCountryCode(value: unknown): CountryCode | null {
  if (typeof value !== 'string') return null;
  const upper = value.trim().toUpperCase();
  return COUNTRY_CODE_SET.has(upper) ? (upper as CountryCode) : null;
}

/** The catalogue's home market, offered first in the country picker. */
export const DEFAULT_COUNTRY_CODE: CountryCode = 'ET';

const REDECLARE_AFTER_MS = 24 * 30 * 24 * 60 * 60 * 1000;

/**
 * A band is a point-in-time statement that silently ages out of accuracy —
 * someone who declared `AGE_13_17` may be an adult two years later. True when
 * the player should be asked again: never declared, or declared long enough
 * ago that the answer can no longer be trusted to be current.
 */
export function shouldRequestDemographics(
  declaredAt: Date | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!declaredAt) return true;
  return now.getTime() - declaredAt.getTime() >= REDECLARE_AFTER_MS;
}

/** The stored columns, structurally — this package holds no database types. */
export interface DeclaredDemographicsRow {
  ageBand?: AgeBand | null;
  gender?: Gender | null;
  countryCode?: string | null;
  demographicsDeclaredAt?: Date | null;
}

export interface Demographics {
  ageBand: AgeBand | null;
  gender: Gender | null;
  countryCode: string | null;
  declaredAt: string | null;
}

/**
 * Projects the stored columns into the shape every surface reports. Nothing is
 * derived from a declaration: every player is served identically regardless of
 * what they declare, by decision of 2026-08-18. These values are audience
 * description only.
 */
export function toDemographics(row: DeclaredDemographicsRow): Demographics {
  return {
    ageBand: row.ageBand ?? null,
    gender: row.gender ?? null,
    countryCode: row.countryCode ?? null,
    declaredAt: row.demographicsDeclaredAt ? row.demographicsDeclaredAt.toISOString() : null,
  };
}
